import { useCallback, useEffect } from "react";
import { CHAPTER_COUNT } from "../data/experience";
import { stepProductFocus } from "../lib/product-focus";
import { useExperienceStore } from "../store/experience-store";
import { usePageVisible } from "./use-page-visible";

interface ShowroomControlOptions {
  readonly scrollToChapter: (index: number, immediate?: boolean, duration?: number) => void;
  readonly scrollToEnd: (duration: number, onComplete: () => void) => void;
  readonly cancelProgrammaticScroll: () => void;
}

export function useShowroomControls({
  cancelProgrammaticScroll, scrollToChapter, scrollToEnd,
}: ShowroomControlOptions): () => void {
  const autoPlay = useExperienceStore((store) => store.autoPlay);
  const pageVisible = usePageVisible();
  const toggleAutoPlay = useCallback((): void => {
    cancelProgrammaticScroll();
    const store = useExperienceStore.getState();
    store.setAutoPlay(!store.autoPlay);
  }, [cancelProgrammaticScroll]);

  useEffect(() => {
    let resumeTimer: number | undefined;
    let lastInput = 0;
    const clearResume = (): void => {
      window.clearTimeout(resumeTimer);
      resumeTimer = undefined;
    };
    const resumeWhenIdle = (): void => {
      resumeTimer = undefined;
      const remaining = 18_000 - (performance.now() - lastInput);
      if (document.hidden) return;
      if (remaining > 0) {
        resumeTimer = window.setTimeout(resumeWhenIdle, remaining);
      } else {
        useExperienceStore.getState().setAutoPlay(true);
      }
    };
    const pauseForInput = (event?: Event): void => {
      if (event?.target instanceof HTMLElement &&
          event.target.closest("[data-playback-toggle]")) {
        clearResume();
        return;
      }
      const store = useExperienceStore.getState();
      if (store.autoPlay) {
        cancelProgrammaticScroll();
        store.setAutoPlay(false);
      }
      lastInput = performance.now();
      if (resumeTimer === undefined) {
        resumeTimer = window.setTimeout(resumeWhenIdle, 18_000);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      const target = event.target;
      // Native controls own their Space/arrow/Home keys, including the assembly slider.
      if (target instanceof HTMLElement && target.closest(
        "input, textarea, select, [contenteditable='true']",
      )) {
        pauseForInput(event);
        return;
      }
      if ((event.key === " " || event.key === "Enter") &&
          target instanceof HTMLElement && target.closest("button, a")) {
        pauseForInput(event);
        return;
      }
      const store = useExperienceStore.getState();
      const chapter = store.chapterIndex;
      if (chapter === 4 && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
        event.preventDefault();
        pauseForInput();
        store.setFocusedProductIndex(stepProductFocus(store.focusedProductIndex,
          event.key === "ArrowDown" ? 1 : -1));
        return;
      }
      if (chapter === 4 && event.key === "Escape") {
        pauseForInput();
        store.setFocusedProductIndex(null);
        return;
      }
      const next = event.key === "ArrowRight" || event.key === "PageDown";
      const previous = event.key === "ArrowLeft" || event.key === "PageUp";
      if (next || previous || event.key === "Home" || event.key === "End") {
        event.preventDefault();
        pauseForInput();
        cancelProgrammaticScroll();
        scrollToChapter(event.key === "Home" ? 0 : event.key === "End"
          ? CHAPTER_COUNT - 1 : chapter + (next ? 1 : -1));
        return;
      }
      if (event.key === " ") {
        event.preventDefault();
        clearResume();
        toggleAutoPlay();
        return;
      }
      pauseForInput();
    };
    const onVisibility = (): void => {
      clearResume();
      if (document.hidden) cancelProgrammaticScroll();
      else if (!useExperienceStore.getState().autoPlay) pauseForInput();
    };
    // Capture wheel before Lenis consumes it, so a new wheel delta isn't cancelled.
    window.addEventListener("wheel", pauseForInput, { passive: true, capture: true });
    window.addEventListener("pointerdown", pauseForInput, { passive: true });
    window.addEventListener("pointermove", pauseForInput, { passive: true });
    window.addEventListener("touchstart", pauseForInput, { passive: true });
    window.addEventListener("keydown", onKeyDown);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearResume();
      window.removeEventListener("wheel", pauseForInput, true);
      window.removeEventListener("pointerdown", pauseForInput);
      window.removeEventListener("pointermove", pauseForInput);
      window.removeEventListener("touchstart", pauseForInput);
      window.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [cancelProgrammaticScroll, scrollToChapter, toggleAutoPlay]);

  useEffect(() => {
    document.documentElement.classList.toggle("is-page-hidden", !pageVisible);
    return () => document.documentElement.classList.remove("is-page-hidden");
  }, [pageVisible]);

  useEffect(() => {
    if (!autoPlay || !pageVisible) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const timer = window.setInterval(() => {
        const chapter = useExperienceStore.getState().chapterIndex;
        scrollToChapter((chapter + 1) % CHAPTER_COUNT, true);
      }, 11_000);
      return () => window.clearInterval(timer);
    }
    let active = true;
    const timers = new Set<number>();
    const schedule = (callback: () => void, delay: number): void => {
      const timer = window.setTimeout(() => {
        timers.delete(timer);
        if (active) callback();
      }, delay);
      timers.add(timer);
    };
    const beginContinuousPass = (): void => {
      const remainingScreens = Math.max(1,
        (document.documentElement.scrollHeight - window.innerHeight - window.scrollY) /
          window.innerHeight);
      scrollToEnd(Math.max(8, remainingScreens * 6.2), () => {
        if (!active) return;
        schedule(() => {
          document.documentElement.classList.add("is-loop-resetting");
          schedule(() => {
            scrollToChapter(0, true);
            schedule(() => {
              document.documentElement.classList.remove("is-loop-resetting");
              schedule(beginContinuousPass, 4_000);
            }, 450);
          }, 600);
        }, 5_500);
      });
    };
    schedule(beginContinuousPass, 3_500);
    return () => {
      active = false;
      timers.forEach((timer) => window.clearTimeout(timer));
      document.documentElement.classList.remove("is-loop-resetting");
    };
  }, [autoPlay, pageVisible, scrollToChapter, scrollToEnd]);

  return toggleAutoPlay;
}
